import { Platform } from 'react-native';

/** On the web the app and the cloud service share an address; the phone app is given one at build time. */
const BASE = Platform.OS === 'web' ? '' : (process.env.EXPO_PUBLIC_API_URL ?? 'http://localhost:4000');

export class ApiError extends Error {
  constructor(public status: number, public code: string, public reference?: string) {
    super(code);
  }
}

/**
 * The reference (request id) of the last request that failed, shown as "Referencia: …" next to the
 * error and carried by the Get help email (company standard, rules 2 and section 6).
 */
let lastFailure: { reference?: string; at: number } | null = null;
export function recentFailureReference(): string | undefined {
  return lastFailure && Date.now() - lastFailure.at < 5 * 60_000 ? lastFailure.reference : undefined;
}

let token: string | null = null;
let onSignedOut: () => void = () => {};
let readOnly: (() => void) | null = null;

/** The operator's "view as": changes are refused here, before they reach the service (which refuses them too). */
export function setReadOnly(onChange: (() => void) | null) {
  readOnly = onChange;
}

/**
 * Downloads a file from the service with the owner's sign-in (for "Download my data"). Returns the
 * text and the file name the service suggested.
 */
export async function apiFile(path: string): Promise<{ text: string; name: string }> {
  const res = await fetch(`${BASE}/api${path}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, 'download_failed', res.headers.get('x-request-id') ?? undefined);
  const name = res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'dejo-dicho.json';
  return { text: await res.text(), name };
}

export function setToken(t: string | null) {
  token = t;
}
export function setSignedOutHandler(fn: () => void) {
  onSignedOut = fn;
}

export async function api<T = { ok: true }>(method: 'GET' | 'POST' | 'PATCH' | 'DELETE', path: string, json?: unknown): Promise<T> {
  if (readOnly && method !== 'GET') {
    readOnly();
    throw new ApiError(403, 'read_only_view');
  }
  let res: Response;
  try {
    res = await fetch(`${BASE}/api${path}`, {
      method,
      headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: json === undefined ? undefined : JSON.stringify(json),
    });
  } catch {
    throw new ApiError(0, 'offline');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && token) onSignedOut();
    const reference = res.headers.get('x-request-id') ?? undefined;
    lastFailure = { reference, at: Date.now() };
    throw new ApiError(res.status, (data as { error?: string }).error ?? 'server_error', reference);
  }
  return data as T;
}
